/**
 * 从 CE containers 各模块 router 组装 EE generalScope 菜单的工具。
 * CE 菜单为真理源；EE 仅通过 group/subGroup / 插删补丁差异化。
 */

/**
 * 给 L2 菜单挂上 EE 侧边栏需要的 group / subGroup
 * @param {Array} menus
 * @param {{ group?: string, subGroup?: string, mapMenu?: Function }} options
 */
export function attachGroup (menus = [], options = {}) {
  const { group, subGroup, mapMenu } = options
  return menus.map(menu => {
    const next = { ...menu }
    if (group != null) next.group = group
    if (subGroup != null) next.subGroup = subGroup
    if (menu.submenus) next.submenus = [...menu.submenus]
    if (typeof mapMenu === 'function') {
      return mapMenu(next) || next
    }
    return next
  })
}

/**
 * 按 path 过滤 submenu（或顶层叶子 path），并去掉空的 L2 组
 * @param {Array} menus
 * @param {{ exclude?: string[], include?: string[] }} options
 */
export function filterMenuPaths (menus = [], options = {}) {
  const exclude = new Set(options.exclude || [])
  const include = options.include ? new Set(options.include) : null

  const keepPath = (path) => {
    if (!path) return true
    if (exclude.has(path)) return false
    if (include && !include.has(path)) return false
    return true
  }

  return menus
    .map(menu => {
      if (menu.path && !menu.submenus) {
        return keepPath(menu.path) ? menu : null
      }
      if (!menu.submenus) return menu
      const submenus = menu.submenus.filter(item => keepPath(item.path))
      if (!submenus.length) return null
      return { ...menu, submenus }
    })
    .filter(Boolean)
}

/**
 * 在指定 path 后插入 submenu（只处理第一层带 submenus 的组）
 */
export function insertSubmenuAfter (menus = [], afterPath, item) {
  return menus.map(menu => {
    if (!menu.submenus) return menu
    const idx = menu.submenus.findIndex(s => s.path === afterPath)
    if (idx === -1) return menu
    const submenus = [...menu.submenus]
    submenus.splice(idx + 1, 0, item)
    return { ...menu, submenus }
  })
}

/**
 * 将顶层「叶子 path」并入第一个带子菜单的 L2（用于 Monitor overview 等）
 */
export function mergeLeadingLeavesIntoFirstGroup (menus = []) {
  const leaves = []
  const rest = []
  for (let i = 0; i < menus.length; i++) {
    const m = menus[i]
    if (!rest.length && m.path && !m.submenus) {
      leaves.push(m)
    } else {
      rest.push(m)
    }
  }
  if (!leaves.length || !rest.length) return menus
  const [first, ...others] = rest
  if (!first.submenus) return menus
  return [
    { ...first, submenus: [...leaves, ...first.submenus] },
    ...others,
  ]
}

/**
 * 按 L2 meta.composeGroup 合并菜单组（CE 声明、EE 组装时生效；CE 自身忽略）。
 * - 同 composeGroup 的 L2 合并 submenus
 * - 标题优先用任一 L2 的 meta.composeGroupLabel，否则用该组首次出现的 label
 * - submenu / L2 可用 meta.composeOrder 控制顺序（越小越靠前）
 */
export function mergeByComposeGroup (menus = []) {
  const merged = new Map()
  const result = []

  const sortByComposeOrder = (list) => {
    return list.slice().sort((a, b) => {
      const ao = (a.meta && a.meta.composeOrder != null) ? a.meta.composeOrder : 0
      const bo = (b.meta && b.meta.composeOrder != null) ? b.meta.composeOrder : 0
      return ao - bo
    })
  }

  menus.forEach((menu) => {
    const key = menu.meta && menu.meta.composeGroup
    if (!key) {
      result.push(menu)
      return
    }
    if (!merged.has(key)) {
      const next = {
        ...menu,
        meta: { ...(menu.meta || {}) },
        submenus: menu.submenus ? [...menu.submenus] : [],
      }
      if (next.meta.composeGroupLabel) {
        next.meta.label = next.meta.composeGroupLabel
      }
      merged.set(key, next)
      result.push(next)
      return
    }
    const target = merged.get(key)
    if (menu.meta && menu.meta.composeGroupLabel) {
      target.meta.label = menu.meta.composeGroupLabel
    }
    if (menu.submenus && menu.submenus.length) {
      target.submenus.push(...menu.submenus)
    }
  })

  merged.forEach((menu) => {
    if (menu.submenus && menu.submenus.length) {
      menu.submenus = sortByComposeOrder(menu.submenus)
    }
  })

  return result
}

/**
 * 按 subGroup 配置批量组装多个 CE 模块
 * @param {Array<{ menus: Array, group?: string, subGroup?: string, mapMenu?: Function, exclude?: string[], mergeComposeGroup?: boolean }>} specs
 */
export function composeFromModules (specs = []) {
  const result = []
  specs.forEach(spec => {
    if (!spec || !spec.menus) return
    let menus = attachGroup(spec.menus, {
      group: spec.group,
      subGroup: spec.subGroup,
      mapMenu: spec.mapMenu,
    })
    if (spec.exclude || spec.include) {
      menus = filterMenuPaths(menus, {
        exclude: spec.exclude,
        include: spec.include,
      })
    }
    if (spec.mergeComposeGroup !== false) {
      menus = mergeByComposeGroup(menus)
    }
    if (spec.transform) {
      menus = spec.transform(menus) || menus
    }
    result.push(...menus)
  })
  return result
}
